import React, { useEffect, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import DatePickerInput from "react-native-paper-dates/src/Date/DatePickerInput";
import { Sparkle } from "@phosphor-icons/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Controller, useForm } from "react-hook-form";
import {
  useGenerateDueDatesForActivitiesMutation,
  useGetCourseByIdQuery,
  useUpdateCourseDatesMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";

enum ModalState {
  CLOSED = "CLOSED",
  PROCESSING = "PROCESSING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  GENERATING = "GENERATING",
  GENERATION_SUCCESS = "GENERATION_SUCCESS",
  GENERATION_ERROR = "GENERATION_ERROR",
}

const modalTexts = {
  [ModalState.PROCESSING]: {
    title: "Updating Course Dates",
    ariaLabel: "Updating Course Dates",
    warningText: "Please wait while we update the course dates",
    actionButtonText: "Close",
  },
  [ModalState.SUCCESS]: {
    title: "Course Dates Updated",
    ariaLabel: "Course Dates Updated",
    warningText: "Course dates have been updated",
    actionButtonText: "Close",
  },
  [ModalState.ERROR]: {
    title: "Error Updating Course Dates",
    ariaLabel: "Error Updating Course Dates",
    warningText: "An error occurred while updating the course dates",
    actionButtonText: "Close",
  },
  [ModalState.GENERATING]: {
    title: "Generating Due Dates",
    ariaLabel: "Generating Due Dates",
    warningText: "Please wait while we generate the due dates",
    actionButtonText: "Close",
  },
  [ModalState.GENERATION_SUCCESS]: {
    title: "Due Dates Generated",
    ariaLabel: "Due Dates Generated",
    warningText: "Due dates have been generated",
    actionButtonText: "Close",
  },
  [ModalState.GENERATION_ERROR]: {
    title: "Error Generating Due Dates",
    ariaLabel: "Error Generating Due Dates",
    warningText: "An error occurred while generating the due dates",
    actionButtonText: "Close",
  },
};

const schema = z
  .object({
    startDate: z.date().nullable(),
    endDate: z.date().nullable(),
    dueDateStartDate: z.date().nullable(),
    dueDateEndDate: z.date().nullable(),
  })
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.startDate) <= new Date(data.endDate);
    },
    {
      message: "Start date must be before or equal to end date",
      path: ["startDate", "endDate"],
    }
  )
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.dueDateStartDate || !data.dueDateEndDate) return true;
      return new Date(data.dueDateStartDate) <= new Date(data.dueDateEndDate);
    },
    {
      message: "Due date start must be before or equal to due date end",
      path: ["dueDateStartDate", "dueDateEndDate"],
    }
  );

const DueDateControlsContainer = ({ courseId }: { courseId: string }) => {
  const { data: courseData, refetch: refetchCourseData } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const [updateCourseDates] = useUpdateCourseDatesMutation();
  const [generateDueDatesForActivities] = useGenerateDueDatesForActivitiesMutation();

  const [dueDateOption, setDueDateOption] = useState<"all">("all");
  const [canGenerateDueDates, setCanGenerateDueDates] = useState(false);

  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const { control, handleSubmit, setValue, formState, reset } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      startDate: null,
      endDate: null,
      dueDateStartDate: null,
      dueDateEndDate: null,
    },
  });

  useEffect(() => {
    if (courseData?.getCourseById?.data?.[0]) {
      const course = courseData.getCourseById.data[0];

      if (course.startDate) setValue("startDate", new Date(courseData.getCourseById.data[0].startDate));

      if (course.endDate) setValue("endDate", new Date(courseData.getCourseById.data[0].endDate));

      if (course.dueDateStartDate)
        setValue("dueDateStartDate", new Date(courseData.getCourseById.data[0].dueDateStartDate));

      if (course.dueDateEndDate) setValue("dueDateEndDate", new Date(courseData.getCourseById.data[0].dueDateEndDate));
    }
  }, [courseData]);

  useEffect(() => {
    if (courseData?.getCourseById?.data?.[0]) {
      const course = courseData.getCourseById.data[0];
      setCanGenerateDueDates(course.dueDateStartDate && course.dueDateEndDate);
    }
  }, [courseData]);

  const handleUpdateCourseDates = async () => {
    if (formState.isValid) {
      setModalState(ModalState.PROCESSING);
    }

    handleSubmit(async (data) => {
      const result = await updateCourseDates({
        variables: {
          id: courseId,
          input: {
            startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
            endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
            dueDateStartDate: data.dueDateStartDate ? new Date(data.dueDateStartDate).toISOString() : null,
            dueDateEndDate: data.dueDateEndDate ? new Date(data.dueDateEndDate).toISOString() : null,
          },
        },
      });

      if (result.data?.updateCourseDates.success) {
        setModalState(ModalState.SUCCESS);
      } else {
        setModalState(ModalState.ERROR);
      }
      reset();
      await refetchCourseData();
    })();
  };

  const handleGenerateDueDates = async () => {
    setModalState(ModalState.GENERATING);

    const result = await generateDueDatesForActivities({
      variables: {
        courseId,
      },
    });

    if (result.data?.generateDueDatesForActivities.success) {
      setModalState(ModalState.GENERATION_SUCCESS);
    } else {
      setModalState(ModalState.GENERATION_ERROR);
    }

    await refetchCourseData();
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <CustomText text="Course Start and End Dates" size="m" weight="ultraBold" style={styles.sectionTitle} />
        <CustomText
          text="These dates determine when your course becomes available to students and when it will close."
          size="xs"
          style={styles.sectionDescription}
        />
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.row}>
          <View style={styles.dateFieldContainer}>
            <CustomText text="Start Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="start"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="Birthday date picker"
                />
              )}
            />
          </View>
          <View style={styles.dateFieldContainer}>
            <CustomText text="End Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="endDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="end"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="End date picker"
                />
              )}
            />
          </View>
          <CustomButton
            text="Set"
            styleType="primary"
            size="m"
            onPress={handleUpdateCourseDates}
            style={{
              backgroundColor: !formState.isValid || !formState.isDirty ? "#D1D5DB" : "#4F46E5",
              alignSelf: "flex-end",
            }}
            disabled={!formState.isValid || !formState.isDirty}
          />
        </View>
        <View style={styles.divider} />
        <CustomText
          text="Update Due Dates for Course Activities"
          size="m"
          weight="ultraBold"
          style={styles.sectionTitle}
        />
        <CustomText
          text="Based on your selected start and end dates, Axio will automatically generate evenly spaced due dates across all modules and activities. You can adjust any due date manually afterward."
          size="xs"
          style={styles.sectionDescription}
        />
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.row}>
          <View style={styles.dateFieldContainer}>
            <CustomText text="Start Due Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="dueDateStartDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="start"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="Start due date picker"
                />
              )}
            />
          </View>
          <View style={styles.dateFieldContainer}>
            <CustomText text="End Due Date" size="xs" weight="bold" />
            <Controller
              control={control}
              name="dueDateEndDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  locale="en"
                  label={undefined}
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                    onBlur();
                  }}
                  inputMode="end"
                  mode="outlined"
                  withDateFormatInLabel={false}
                  outlineColor={"#D1D5DB"}
                  activeOutlineColor={"blue"}
                  inputProps={{
                    color: "#DEDEDE",
                    mode: "outline",
                  }}
                  theme={{
                    colors: "#000000",
                    roundness: 8,
                    opacity: 1,
                  }}
                  aria-label="End due date picker"
                />
              )}
            />
          </View>
          <CustomButton
            text="Set"
            styleType="primary"
            size="m"
            onPress={handleUpdateCourseDates}
            style={{
              backgroundColor: !formState.isValid || !formState.isDirty ? "#D1D5DB" : "#4F46E5",
              alignSelf: "flex-end",
            }}
            disabled={!formState.isValid || !formState.isDirty}
          />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.radioGroup}>
          <Pressable
            style={styles.radioOption}
            onPress={() => setDueDateOption("all")}
            accessibilityRole="radio"
            accessibilityState={{ selected: dueDateOption === "all" }}>
            <View style={[styles.radioCircle, dueDateOption === "all" && styles.radioCircleSelected]} />
            <CustomText text="Regenerate due dates for all activities" size="xs" style={styles.radioLabel} />
          </Pressable>
          {dueDateOption === "all" && (
            <CustomText text="This will overwrite all existing due dates" size="xs" style={styles.warningText} />
          )}
        </View>
        <CustomSpacing type="vertical" size="l" />
        {!formState.isValid && formState.isDirty && (
          <CustomText
            text={
              "Please check your dates and try again. Start Date should be before End Date, and Start Due Date should be before End Due Date."
            }
            size="xs"
            style={styles.warningText}
          />
        )}
        <CustomSpacing type="vertical" size="l" />
        <CustomButton
          text="Generate Due Dates"
          styleType="outline"
          size="m"
          onPress={handleGenerateDueDates}
          textStyle={{
            color: !(!canGenerateDueDates || formState.isDirty) ? "#4F46E5" : "#D1D5DB",
          }}
          style={{ width: "fit-content", gap: 8 }}
          leftIcon={
            <Sparkle
              size={16}
              weight="bold"
              color={!(!canGenerateDueDates || formState.isDirty) ? "#4F46E5" : "#D1D5DB"}
            />
          }
          rightIcon={null}
          textProps={{}}
          useTranslationText={true}
          disabled={!canGenerateDueDates || formState.isDirty}
        />
      </View>

      <NewCustomAxioActionModal
        visible={modalState === ModalState.PROCESSING}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.PROCESSING}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.SUCCESS}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.SUCCESS}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.ERROR}
      />

      <NewCustomAxioActionModal
        visible={modalState === ModalState.GENERATING}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.GENERATING}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.GENERATION_SUCCESS}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.GENERATION_SUCCESS}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.GENERATION_ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.GENERATION_ERROR}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    backgroundColor: "#fff",
    width: 600,
    marginHorizontal: "auto",
    flexDirection: "column",
  },
  card: {
    flexDirection: "column",
    justifyContent: "flex-start",
    maxWidth: 600,
    padding: 24,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    backgroundColor: "#fff",
  },
  sectionTitle: {
    marginBottom: 4,
  },
  sectionDescription: {
    color: "#6B7280",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 32,
    marginBottom: 16,
  },
  dateFieldContainer: {
    flex: 1,
    minWidth: 180,
    maxWidth: 220,
    gap: 4,
  },

  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 24,
    width: "100%",
  },
  radioGroup: {
    flexDirection: "column",
    gap: 8,
    marginTop: 8,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 2,
  },
  radioCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    backgroundColor: "#fff",
    marginRight: 8,
  },
  radioCircleSelected: {
    borderColor: "#4F46E5",
    backgroundColor: "#4F46E5",
  },
  radioLabel: {
    color: "#111827",
  },
  warningText: {
    color: "#EF4444",
    marginLeft: 24,
    marginBottom: 4,
  },
});

export default DueDateControlsContainer;
